import apiClient from './api';

/**
 * Frontend error reporter -- sends uncaught JS errors to the backend
 * so they appear in server logs for log watchers to pick up.
 *
 * Errors are fire-and-forget (we never let a reporting failure
 * cascade into another user-visible error).
 */

// Simple dedup: skip the same message if it was reported in the last 10 s.
const _recentErrors = new Map();
const DEDUP_WINDOW_MS = 10_000;

function isDuplicate(message) {
  const now = Date.now();
  const prev = _recentErrors.get(message);
  if (prev && now - prev < DEDUP_WINDOW_MS) {
    return true;
  }
  _recentErrors.set(message, now);
  // Prune old entries to avoid unbounded growth
  if (_recentErrors.size > 50) {
    for (const [key, ts] of _recentErrors) {
      if (now - ts > DEDUP_WINDOW_MS) _recentErrors.delete(key);
    }
  }
  return false;
}

// Expected axios API failures that are NORMAL operation, not client bugs:
//   * 401 -- requests fired before login (pre-auth)
//   * 404 -- non-existent RC-* pseudo-parcel lookups
//   * 422 -- request-body validation
// These arrive as axios' "Request failed with status code NNN" messages and
// were drowning the CLIENT_ERROR stream (~778 lines / 2 min, #1060). Skip
// reporting them. Every 5xx and every non-axios message still flows through.
const _EXPECTED_API_ERROR_RE = /Request failed with status code (401|404|422)\b/;

export function isExpectedApiError(message) {
  return _EXPECTED_API_ERROR_RE.test(String(message));
}

/**
 * Report an error to the backend.
 *
 * @param {Object} opts
 * @param {string} opts.message   - Human-readable error message (required)
 * @param {string} [opts.stack]   - JS stack trace
 * @param {string} [opts.source]  - Where the error originated (e.g. "ErrorBoundary", "window.onerror")
 * @param {string} [opts.componentStack] - React component stack (from componentDidCatch)
 * @param {string} [opts.level]   - "error" (default) or "warn"
 */
export function reportError({ message, stack, source, componentStack, level }) {
  if (!message || isDuplicate(message)) return;
  // De-noise: don't report expected axios 4xx (pre-auth 401 / missing RC-*
  // 404 / 422 validation) as client errors -- they are normal, not bugs.
  if (isExpectedApiError(message)) return;

  const payload = {
    message: String(message).slice(0, 2000),
    stack: stack ? String(stack).slice(0, 10000) : undefined,
    source: source || undefined,
    url: window.location.href,
    user_agent: navigator.userAgent,
    timestamp: new Date().toISOString(),
    component_stack: componentStack ? String(componentStack).slice(0, 5000) : undefined,
    level: level || 'error',
  };

  // Fire-and-forget -- never let this throw or surface to the user
  apiClient.post('/admin/client-errors', payload).catch(() => {});
}

/**
 * Install global listeners for uncaught errors and unhandled rejections.
 * Call once at app startup (index.js).
 */
export function installGlobalErrorHandlers() {
  window.onerror = (message, source, lineno, colno, error) => {
    reportError({
      message: String(message),
      stack: error?.stack || `${source}:${lineno}:${colno}`,
      source: 'window.onerror',
    });
    // Return false so the browser still logs to console
    return false;
  };

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    reportError({
      message: reason?.message || String(reason),
      stack: reason?.stack,
      source: 'unhandledrejection',
    });
  });
}
